import React from "react";
import { useWizard } from "@/context/WizardContext";
import { Grid, TextInput, SectionHeading, FieldLabel, SelectInput } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";
import { PRN_TEMPLATE } from "@/lib/dhsRules";

const DELEGATED_OPTIONS = [
  "Medication set-up (RN)",
  "Medication administration (delegated)",
  "Blood pressure monitoring (delegated)",
  "Blood glucose monitoring (delegated)",
  "Oxygen maintenance (delegated)",
  "Enteral feeding (delegated)",
  "Catheter care (delegated)",
  "Ostomy care (delegated)",
];

const INCIDENTAL_OPTIONS = [
  "Light housekeeping",
  "Meal preparation",
  "Laundry",
  "Shopping (essential)",
  "Escort to medical appointments",
];

const PART_II_TASKS = [
  { key: "glucometer", label: "Glucometer Readings" },
  { key: "skin_care", label: "Skin Care (Application of prescription medications)" },
  { key: "catheter", label: "Catheter Site Care (Suprapubic only)" },
  { key: "feeding_tube", label: "Feeding Tube Site Care" },
  { key: "complex_position", label: "Complex Positioning" },
];

const PART_III_TASKS = [
  { key: "tube_continuous", label: "Daily Tube Feedings — Continuous" },
  { key: "tube_bolus", label: "Daily Tube Feedings — Intermittent (Bolus)" },
  { key: "trach", label: "Respiratory: Tracheostomy Care" },
  { key: "suction", label: "Respiratory: Suctioning" },
  { key: "chest_pt", label: "Respiratory: Chest Physiotherapy" },
  { key: "nebulizer", label: "Respiratory: Nebulizer" },
  { key: "suppository", label: "Bowel Program: Suppository" },
  { key: "enema", label: "Bowel Program: Enema" },
  { key: "digital_stim", label: "Bowel Program: Digital Stimulation" },
  { key: "wound", label: "Wound Care" },
  { key: "rom", label: "Range of Motion" },
  { key: "vitals", label: "Vital Signs" },
];

function ChipList({ label, selected = [], options, onChange, testidPrefix }) {
  const toggle = (opt) => {
    const next = selected.includes(opt) ? selected.filter((o) => o !== opt) : [...selected, opt];
    onChange(next);
  };
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-chips`}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button type="button" key={o} onClick={() => toggle(o)}
              data-testid={`${testidPrefix}-chip-${o.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3.5 h-10 rounded-full text-[13px] font-medium transition-all border
                ${on ? "bg-[#0A3D79] text-[#F7FAFC] border-[#0A3D79]" : "bg-white text-[#0B1E3A] border-[#D6E3EF] hover:border-[#1FA0E6]"}`}>
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TaskRow({ taskKey, label, part, onChange, testidPrefix }) {
  const row = part[taskKey] || {};
  const set = (patch) => onChange({ ...part, [taskKey]: { ...row, ...patch } });
  return (
    <div className="rounded-xl border border-[#D6E3EF] p-4 bg-white grid grid-cols-1 md:grid-cols-[auto,1fr,140px,140px] gap-4 items-center" data-testid={`${testidPrefix}-${taskKey}`}>
      <button type="button" onClick={() => set({ selected: !row.selected })}
        className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 border ${row.selected ? "bg-[#0A3D79] border-[#0A3D79] text-[#F7FAFC]" : "bg-white border-[#B7CADA]"}`}
        data-testid={`${testidPrefix}-${taskKey}-toggle`}
        aria-label={row.selected ? "selected" : "not selected"}>
        {row.selected && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
      </button>
      <div className="text-[14px] text-[#0B1E3A] font-medium">{label}</div>
      <TextInput placeholder="Freq/Day" value={row.frequency_per_day}
        onChange={(v) => set({ frequency_per_day: v })}
        testid={`${testidPrefix}-${taskKey}-fpd`} />
      <TextInput placeholder="Days/Week" value={row.days_per_week}
        onChange={(v) => set({ days_per_week: v })}
        testid={`${testidPrefix}-${taskKey}-dpw`} />
    </div>
  );
}

export default function Step3Nursing() {
  const { data, update } = useWizard();
  const setPart = (part, next) => update("nursing", { [part]: next });

  return (
    <div className="space-y-10">
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 3 · Free-text narrative" title="Nursing Tasks & Delegated Acts"
          description="Document nursing oversight, delegated acts under DHS 107.112, and incidental services." />
        <Grid cols={2}>
          <TextInput label="Medication Administration Plan" testid="nursing-meds-admin"
            value={data.nursing.medications_admin} onChange={(v) => update("nursing", { medications_admin: v })} />
          <TextInput label="Injectable Medications" testid="nursing-injectables"
            value={data.nursing.injectables} onChange={(v) => update("nursing", { injectables: v })} />
          <TextInput label="Wound Care" testid="nursing-wound"
            value={data.nursing.wound_care} onChange={(v) => update("nursing", { wound_care: v })} />
          <TextInput label="Catheter Care" testid="nursing-cath"
            value={data.nursing.catheter_care} onChange={(v) => update("nursing", { catheter_care: v })} />
          <TextInput label="Ostomy Care" testid="nursing-ostomy"
            value={data.nursing.ostomy_care} onChange={(v) => update("nursing", { ostomy_care: v })} />
        </Grid>
        <div className="mt-6 space-y-6">
          <ChipList label="Delegated Nursing Acts" options={DELEGATED_OPTIONS} testidPrefix="nursing-delegated"
            selected={data.nursing.delegated_acts} onChange={(v) => update("nursing", { delegated_acts: v })} />
          <ChipList label="Incidental Services" options={INCIDENTAL_OPTIONS} testidPrefix="nursing-incidental"
            selected={data.nursing.incidental} onChange={(v) => update("nursing", { incidental: v })} />
          <NarrativeField label="Additional Nursing Notes" testid="nursing-notes"
            fieldKey="nursing.notes"
            fieldPurpose="Any additional RN observations about delegation readiness, PCW coordination, or risk factors relevant to personal care."
            value={data.nursing.notes} onChange={(v) => update("nursing", { notes: v })} />
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 3 · PCST Element 27" title="Part I — Medication Assistance Delegated to PCW" />
        <Grid cols={3}>
          <SelectInput label="Level" testid="mot-med-level"
            value={data.nursing.medication_assistance?.level}
            onChange={(v) => update("nursing", { medication_assistance: { ...(data.nursing.medication_assistance || {}), level: v } })}
            options={[
              { value: "0", label: "0 — Not applicable" },
              { value: "A", label: "A — Independent" },
              { value: "B", label: "B — Needs reminders" },
              { value: "C", label: "C — Help from non-PCW" },
              { value: "D", label: "D — Help from a PCW" },
            ]} />
          <TextInput label="Frequency per Day" testid="mot-med-fpd"
            value={data.nursing.medication_assistance?.frequency_per_day}
            onChange={(v) => update("nursing", { medication_assistance: { ...(data.nursing.medication_assistance || {}), frequency_per_day: v } })} />
          <TextInput label="Days per Week" testid="mot-med-dpw"
            value={data.nursing.medication_assistance?.days_per_week}
            onChange={(v) => update("nursing", { medication_assistance: { ...(data.nursing.medication_assistance || {}), days_per_week: v } })} />
        </Grid>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 3 · PCST Element 28" title="Part II — Delegated Nursing Acts (PCW)"
          description="Toggle each task and enter frequency per day + days per week." />
        <div className="space-y-2">
          {PART_II_TASKS.map((t) => (
            <TaskRow key={t.key} taskKey={t.key} label={t.label}
              part={data.nursing.part_ii || {}}
              onChange={(next) => setPart("part_ii", next)}
              testidPrefix="mot-p2" />
          ))}
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 3 · PCST Element 29" title="Part III — Delegated Nursing Acts (ForwardHealth manual-review)"
          description="Toggle each task and enter frequency per day + days per week." />
        <div className="space-y-2">
          {PART_III_TASKS.map((t) => (
            <TaskRow key={t.key} taskKey={t.key} label={t.label}
              part={data.nursing.part_iii || {}}
              onChange={(next) => setPart("part_iii", next)}
              testidPrefix="mot-p3" />
          ))}
        </div>
        <div className="mt-4">
          <NarrativeField label="Part III — Comments (required for all Part III tasks)" testid="mot-p3-comments"
            fieldKey="nursing.part_iii.comments"
            fieldPurpose="Required narrative supporting every Part III delegated act — nurse training plan, evaluation cadence, safety plan."
            value={data.nursing.part_iii?.comments}
            onChange={(v) => setPart("part_iii", { ...(data.nursing.part_iii || {}), comments: v })} />
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 3 · PCST Elements 30–35" title="Other Considerations"
          description="Incidental services, behaviors, rare conditions, seizures, PRN, and notes." />

        <div className="space-y-5">
          <div>
            <FieldLabel>30. Will services incidental to ADL and delegated nursing acts be performed by the PCW?</FieldLabel>
            <SelectInput value={data.incidental_services_provided}
              onChange={(v) => update("incidental_services_provided", v)}
              options={["Yes","No"]} testid="consider-incidental" />
          </div>

          <div>
            <FieldLabel>31. Does the member exhibit behaviors (&gt;1×/week) that interfere with ADL/MOT?</FieldLabel>
            <Grid cols={2}>
              <SelectInput value={data.behaviors?.present}
                onChange={(v) => update("behaviors", { present: v })}
                options={["Yes","No"]} testid="consider-behaviors-yn" />
              <TextInput label="Description" testid="consider-behaviors-desc"
                value={data.behaviors?.description}
                onChange={(v) => update("behaviors", { description: v })} />
            </Grid>
          </div>

          <div>
            <FieldLabel>32. Rare medical condition making ADL/MOT more time-consuming long-term?</FieldLabel>
            <Grid cols={2}>
              <SelectInput value={data.rare_condition?.present}
                onChange={(v) => update("rare_condition", { present: v })}
                options={["Yes","No"]} testid="consider-rare-yn" />
              <TextInput label="Condition, dx code, precautions" testid="consider-rare-desc"
                value={data.rare_condition?.description}
                onChange={(v) => update("rare_condition", { description: v })} />
            </Grid>
          </div>

          <div>
            <FieldLabel>33. Seizures</FieldLabel>
            <Grid cols={3}>
              <SelectInput label="Diagnosed" value={data.seizures?.present}
                onChange={(v) => update("seizures", { present: v })}
                options={["Yes","No"]} testid="consider-seizures-yn" />
              <SelectInput label="Date of last seizure"
                value={data.seizures?.last_seizure_category}
                onChange={(v) => update("seizures", { last_seizure_category: v })}
                options={[
                  { value: "A", label: "A — 0–90 days ago" },
                  { value: "B", label: "B — 91–180 days ago" },
                  { value: "C", label: "C — >180 days ago" },
                ]}
                testid="consider-seizures-category" />
              <TextInput label="Type" testid="consider-seizures-type"
                value={data.seizures?.type}
                onChange={(v) => update("seizures", { type: v })} />
              <TextInput label="Frequency" testid="consider-seizures-freq"
                value={data.seizures?.frequency}
                onChange={(v) => update("seizures", { frequency: v })} />
              <TextInput label="Last date" type="date" testid="consider-seizures-last"
                value={data.seizures?.last_date}
                onChange={(v) => update("seizures", { last_date: v })} />
              <SelectInput label="PCW interventions" value={data.seizures?.interventions_yn}
                onChange={(v) => update("seizures", { interventions_yn: v })}
                options={["Yes","No"]} testid="consider-seizures-inter-yn" />
            </Grid>
            <div className="mt-3">
              <NarrativeField label="Interventions detail" testid="consider-seizures-inter"
                fieldKey="seizures.interventions"
                fieldPurpose="PCW interventions for seizure activity — first-aid steps, protective positioning, post-ictal care."
                value={data.seizures?.interventions}
                onChange={(v) => update("seizures", { interventions: v })} />
            </div>
          </div>

          <div>
            <FieldLabel>34. Pro Re Nata (PRN)</FieldLabel>
            <Grid cols={2}>
              <SelectInput label="Requested" value={data.prn?.requested}
                onChange={(v) => update("prn", { requested: v })}
                options={["Yes","No"]} testid="consider-prn-yn" />
              <TextInput label="Rationale for PRN time" testid="consider-prn-desc"
                value={data.prn?.description}
                onChange={(v) => update("prn", { description: v })} />
            </Grid>
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => update("prn", { description: PRN_TEMPLATE })}
                className="h-9 px-3 rounded-md border border-[#b6c9dc] bg-[#eaf1f7] text-[#0f3b5f] text-[12.5px] font-medium hover:bg-[#dce9f3]"
                data-testid="consider-prn-template"
              >
                Insert DHS PRN template
              </button>
              <span className="text-[11.5px] text-[#3A5275]">
                DHS Element 34: PRN must be phrased exactly as
                <em className="ml-1">"{PRN_TEMPLATE}"</em>
              </span>
            </div>
          </div>

          <NarrativeField label="35. Notes" testid="consider-notes"
            fieldKey="pcst_notes"
            fieldPurpose="Final PCST comments covering anything not captured elsewhere — clinical reasoning, exceptions, coordination."
            value={data.pcst_notes} onChange={(v) => update("pcst_notes", v)} />
        </div>
      </section>
    </div>
  );
}
